import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "../../../shared/ui";
import { groupDigits } from "../../../shared/lib/format";
import {
  AppBar,
  AppCard,
  Banner,
  BottomSheet,
  Button,
  DashedBox,
  FooterSummary,
  PhotoThumb,
  PickField,
  SectionLabel,
  Segmented,
  SignaturePad,
  StatRow,
  Stepper,
  StickyFooter,
  TextField,
  type SignStroke,
} from "./kit";
import { FIN, SIGN, type StrokePoint } from "./data";
import { ScreenHost } from "./ScreenHost";
import { useScript } from "./useScript";
import "./create.css";

// Handoff screens 2 & 3 (how-screens.html `HowCreate`): the «Yangi shartnoma» wizard on step 3/4
// (Moliya — the price is typed, the term flips 6 → 12) and step 4/4 (Hujjatlar — the signature sheet
// opens, the client signs, «Yuborish» closes it and a toast confirms).
// Timings, stroke paths and formatting are module-level so render stays pure (SSG contract, landing.md §4).

// ---- Timeline (ms from mount, handoff setTimeout values) -----------------------------------

/** fin: digit i of "9600" lands at 550 + i·260 (550, 810, 1070, 1330). */
const FIN_TYPE_AT = 550;
const FIN_TYPE_STEP = 260;
/** fin: Segmented 6 → 12 oy. */
const FIN_TERM_AT = 2050;

/** sign: kit.sheet(<BottomSheet>) at 450. */
const SHEET_AT = 450;
/** sign: draw() starts at 1250; one synthetic pointermove per 15 ms, pointerup +10, 120 between strokes. */
const DRAW_AT = 1250;
const PEN_STEP = 15;
const PEN_UP = 10;
const PEN_GAP = 120;
/** sign: «Yuborish» .click() 450 ms after the last stroke's gap. */
const SEND_AFTER = 450;
/** Scripted finger-down on «Yuborish» before the click (the handoff clicks with no press visual). */
const SEND_PRESS = 150;
/** BottomSheet slide-down (tokens.css --dur-sheet), then the sheet unmounts. */
const SHEET_OUT = 360;

const [STROKE_1, STROKE_2] = SIGN.STROKES;
/** Stroke 1 (70 points): pointerdown 1250, last move 2285, pointerup 2295. */
const D1 = DRAW_AT;
const E1 = D1 + (STROKE_1.length - 1) * PEN_STEP + PEN_UP;
/** Stroke 2 (18 points): pointerdown 2415, last move 2670, pointerup 2680. */
const D2 = E1 + PEN_GAP;
const E2 = D2 + (STROKE_2.length - 1) * PEN_STEP + PEN_UP;
/** First pointermove → SignaturePad `drawn` (placeholder hides, grey border, «Yuborish» enabled): 1265. */
const DRAWN_AT = D1 + PEN_STEP;
/** «Yuborish» click: 2680 + 120 + 450 = 3250. */
const SEND_AT = E2 + PEN_GAP + SEND_AFTER;

// ---- Signature strokes ------------------------------------------------------------------------

/** Pad drawing box: 390 − 2·20 (--gutter-sheet) − 2·1.5 (dashed border) = 347 wide, 220 tall (content-box). */
const PAD_W = 347;
const PAD_H = 220;

const toPath = (pts: readonly StrokePoint[]) =>
  pts.map(([x, y], i) => (i ? "L" : "M") + (x * PAD_W).toFixed(1) + " " + (y * PAD_H).toFixed(1)).join(" ");

/** The ink grows from pointerdown to the last pointermove; delays count from the pad's mount (sheet open). */
const PAD_STROKES: readonly SignStroke[] = [
  { d: toPath(STROKE_1), dur: (STROKE_1.length - 1) * PEN_STEP, delay: D1 - SHEET_AT },
  { d: toPath(STROKE_2), dur: (STROKE_2.length - 1) * PEN_STEP, delay: D2 - SHEET_AT },
];

// ---- Script phases -------------------------------------------------------------------------------

// fin: 0 = empty price, 1..4 = FIN.typed[0..3], 5 = term switched to 12.
const FIN_LAST = FIN.typed.length + 1;
// sign: 0 rest, 1 sheet open, 2 pen down (drawn), 3 «Yuborish» pressed, 4 signed + sheet closing, 5 sheet gone.
const SIGN_LAST = 5;

const STEP_KEYS = ["step_client", "step_type", "step_finance", "step_docs"] as const;

/** kitFm: "$" + rounded amount, thousands grouped by groupDigits (deterministic). */
const usd = (n: number) => "$" + groupDigits(Math.round(n));

interface CreateScreenProps {
  mode: "fin" | "sign";
  /** Run the scripted demo once (the phone remounts the screen to replay). */
  play: boolean;
  /** Reduced motion: render the final state, no timers. */
  still: boolean;
}

export function CreateScreen({ mode, play, still }: CreateScreenProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "how.app.create" });
  const fin = mode === "fin";
  const [phase, setPhase] = useState(0);

  const toast = useScript(play, ({ later, toast: showToast }) => {
    if (fin) {
      FIN.typed.forEach((_, i) => later(() => setPhase(i + 1), FIN_TYPE_AT + i * FIN_TYPE_STEP));
      later(() => setPhase(FIN_LAST), FIN_TERM_AT);
      return;
    }
    later(() => setPhase(1), SHEET_AT);
    later(() => setPhase(2), DRAWN_AT);
    later(() => setPhase(3), SEND_AT - SEND_PRESS);
    later(() => {
      setPhase(4);
      showToast(t("toast_signed"));
    }, SEND_AT);
    later(() => setPhase(5), SEND_AT + SHEET_OUT);
  });

  // Final state is derived, never set: no setState outside the timers.
  const at = still ? (fin ? FIN_LAST : SIGN_LAST) : phase;

  // Price / term: fin types them in; sign starts from the finished Moliya step (9 600, 12 oy).
  const price = fin ? (at > 0 ? FIN.typed[Math.min(at, FIN.typed.length) - 1] : "") : FIN.typed[FIN.typed.length - 1];
  const term = fin && at < FIN_LAST ? FIN.termFrom : FIN.termTo;
  const p = Number(price.replace(/\D/g, ""));
  const monthly = usd(p / term);

  const step = fin ? 3 : 4;
  const steps = STEP_KEYS.map((k) => t(k));
  const rise = still ? undefined : "ns-rise";

  const sheetOn = !fin && at >= 1 && at <= 4;
  const sheet = sheetOn ? (
    <BottomSheet title={t("signature")} sub={SIGN.sub} closing={at === 4}>
      <SignaturePad
        hint={t("sign_hint")}
        placeholder={t("sign_here")}
        clearLabel={t("clear")}
        sendLabel={t("send")}
        width={PAD_W}
        height={PAD_H}
        strokes={PAD_STROKES}
        drawn={at >= 2}
        sendPressed={at === 3}
      />
    </BottomSheet>
  ) : null;
  const signed = !fin && at >= 4;

  return (
    <ScreenHost sheet={sheet} toast={toast}>
      <div className="hk-cr-head">
        <AppBar border={false} title={t("title")} sub={t("step_of", { n: step, name: steps[step - 1] })} />
        <Stepper steps={steps} current={step} />
      </div>
      <div className="hk-scroll">
        <div className={"hk-cr-body" + (rise ? " " + rise : "")}>
          {fin ? (
            <>
              <TextField label={t("price")} req lg prefix="$" value={price} placeholder="0" />
              <TextField label={t("down")} optional={t("optional")} prefix="$" placeholder="0" />
              {/* Same markup as the kit's FieldLabel (not exported) above the Segmented. */}
              <div className="hk-field">
                <div className="hk-label">
                  <span>
                    {t("term")}
                    <b>*</b>
                  </span>
                </div>
                <Segmented items={FIN.terms.map((n) => t("months", { n }))} on={FIN.terms.indexOf(term)} />
              </div>
              <PickField label={t("first_date")} req icon="i-cal" value={FIN.firstDate} />
              {p > 0 && (
                <AppCard className="tight hk-cr-sum">
                  <StatRow label={t("monthly")} value={monthly} tone="positive" />
                  <StatRow label={t("count")} value={t("count_value", { n: term })} />
                  <StatRow total label={t("total")} value={usd(p)} divider={false} />
                </AppCard>
              )}
            </>
          ) : (
            <>
              <div className="hk-cr-group">
                <SectionLabel title={t("photos")} sub={t("photos_hint")} />
                <div className="hk-cr-photos">
                  <PhotoThumb label={t("photo", { n: 1 })} />
                  <PhotoThumb label={t("photo", { n: 2 })} />
                  <PhotoThumb add label={t("add")} />
                </div>
              </div>
              <div className="hk-cr-group">
                <SectionLabel title={t("signature")} />
                {signed ? (
                  <AppCard className="compact">
                    <div className={"hk-cr-signed" + (rise ? " " + rise : "")}>
                      <Icon name="i-check" />
                      <span className="hk-cr-signed-l">{t("signed")}</span>
                      <Button variant="ghost" sm>
                        {t("redo")}
                      </Button>
                    </div>
                  </AppCard>
                ) : (
                  <DashedBox className="hk-cr-cta">
                    <Icon name="i-pen" />
                    {t("sign_cta")}
                  </DashedBox>
                )}
              </div>
              <Banner tone="info">{t("sms_note")}</Banner>
            </>
          )}
        </div>
      </div>
      <StickyFooter back summary={p > 0 ? <FooterSummary label={t("monthly")} value={`${monthly} × ${term}`} /> : undefined}>
        <Button grow>{fin ? t("next") : t("create")}</Button>
      </StickyFooter>
    </ScreenHost>
  );
}
